import type {ResolvableScript} from '@unhead/vue';
import {useRequestHeaders, useRoute} from '#imports';
import {useDataLayer} from '../composables/useDataLayer';
import type {HostTrackingSettings} from '../types';

export function isRequestFromBot(headers: Readonly<Record<string, string | undefined>>) {
    const maltBotScore = Number(headers['x-malt-bot-score']);
    return maltBotScore <= 40;
}

async function gtmFbqScriptAsString(): Promise<string | null> {
    try {
        const {gtmFbqScript} = await import('../scripts/gtm');
        return `${gtmFbqScript}
        gtmFbqScript();`;
    } catch {
        return null;
    }
}

async function gtmDataLayerScriptAsString(googleTagManagerId: string, dataLayer: string): Promise<string | null> {
    try {
        const {gtmDataLayerScript} = await import('../scripts/gtm');
        return `${gtmDataLayerScript}
        gtmDataLayerScript('${googleTagManagerId}', ${dataLayer})`;
    } catch {
        return null;
    }
}

export async function useGetGoogleTagManagerScripts(googleTagManagerId: string | undefined, dataLayer: string): Promise<ResolvableScript[]> {
    const isMicroFrontEnd = useRoute().query?.['app-type'] === 'microfrontend';
    if (googleTagManagerId && !isMicroFrontEnd) {
        return [
            {innerHTML: await gtmFbqScriptAsString(), type: 'text/javascript'},
            {innerHTML: await gtmDataLayerScriptAsString(googleTagManagerId, dataLayer), type: 'text/javascript'},
        ];
    }
    return [];
}

export function useScripts(hostSettings?: HostTrackingSettings | null) {
    const dataLayer = useDataLayer();
    const dataLayerAsString = JSON.stringify(dataLayer.value);

    async function loadScripts(): Promise<ResolvableScript[]> {
        const headers = useRequestHeaders();
        if (isRequestFromBot(headers)) {
            return [];
        }
        if (!hostSettings) {
            return [];
        }

        return await useGetGoogleTagManagerScripts(hostSettings?.googleTagManagerId, dataLayerAsString);
    }

    return {
        loadScripts,
    };
}
